import { ChevronRight } from "lucide-react";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { translateOptional } from "@/i18n/translate";
import { cn } from "@/lib/utils";
import { Ago, SeverityBadge, SeverityIcon } from "./components/primitives";
import { formatDuration, formatMb, formatMs, formatPercent, formatStamp } from "./format";
import type { MonitoringTab } from "./monitoring-context";
import type { Finding } from "./types";

/** Which tab explains a finding. */
export const AREA_TAB: Record<string, MonitoringTab> = {
  pods: "pods",
  cameras: "cameras",
  pipeline: "pipeline",
  integrations: "pipeline",
  http: "requests",
  backend: "backend",
};

/** Finding params as display strings: durations, shares and lists formatted by their name. */
function formatParams(params: Record<string, unknown>): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) out[key] = "–";
    else if (Array.isArray(value)) out[key] = value.join(", ") || "–";
    else if (typeof value === "number" && (key.endsWith("_ms") || key === "ms")) out[key] = formatMs(value);
    else if (typeof value === "number" && (key === "rate" || key === "share")) out[key] = formatPercent(value);
    else if (typeof value === "number" && key.endsWith("_s")) out[key] = formatDuration(value);
    else if (typeof value === "number" && key.endsWith("_mb")) out[key] = formatMb(value);
    else if (typeof value === "number" || typeof value === "string") out[key] = value;
    else out[key] = JSON.stringify(value);
  }
  return out;
}

/** The sentence of a finding in the UI language (an unknown code falls back to code + params). */
export function findingText(finding: Finding): string {
  const values = formatParams(finding.params ?? {});
  return (
    translateOptional(`monitoring.finding.${finding.code}`, values) ??
    `${finding.code} ${Object.entries(values).map(([k, v]) => `${k}=${v}`).join(" ")}`
  );
}

export function FindingRow({
  finding,
  onOpen,
  compact = false,
}: {
  finding: Finding;
  onOpen?: (tab: MonitoringTab) => void;
  compact?: boolean;
}) {
  const { t } = useLocalization();
  const tab = AREA_TAB[finding.area];
  const areaKey = `monitoring.area.${finding.area}` as TranslationKey;
  const content = (
    <>
      <SeverityIcon severity={finding.severity} className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className={cn("break-words text-sm leading-snug", finding.severity === "info" && "text-muted-foreground")}>
          {findingText(finding)}
        </div>
        {!compact && (
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
            <SeverityBadge severity={finding.severity} label={t(`monitoring.severity.${finding.severity}`)} />
            <span>{translateOptional(areaKey) ?? finding.area}</span>
            {finding.since ? (
              <span title={formatStamp(finding.since)}>
                · {t("monitoring.finding.since", { time: formatStamp(finding.since) })} (<Ago ts={finding.since} />)
              </span>
            ) : null}
          </div>
        )}
      </div>
      {onOpen && tab && <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />}
    </>
  );
  if (onOpen && tab) {
    return (
      <button
        type="button"
        onClick={() => onOpen(tab)}
        className="flex w-full items-start gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
      >
        {content}
      </button>
    );
  }
  return <div className="flex items-start gap-3 px-2 py-2">{content}</div>;
}
